import { Fragment, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { endOfDay, startOfDay } from 'date-fns';
import { CSVLink, Flex, StyledDatePicker, StyledFormSelect, WrappedQuery } from '..';
import {
  ALL_FUNDER_ACTIVITIES,
  ALL_INSTITUTION_ACTIVITIES,
  ALL_INTERVENTIONS,
  ALL_SERVICE_PROVIDERS,
  ALL_SERVICE_PROVIDER_ACTIVITIES,
  EXPORT_INPUTS,
} from '../../../graphql';
import { formatInputStatsData, formatSelectValue, getPortal, sanitizeData } from '../../../utils';
import { GRANULARITY, INPUT_STATS_LEVELS, inputStatsSelectOptions, inputStatsVisibleOptions } from '../../../consts';
import { getAllProgrammesQuery } from '../../../containers/shared/programmes/utils';

type Props = {
  serviceProvider: string;
  activity: string;
  intervention: string;
  startDate: Date;
  endDate: Date;
  programme: string;
  step: string;
};

type State = {
  granularity?: any;
  level?: any;
  programmeIds?: string[];
  serviceProviderIds?: string[];
  interventionIds?: string[];
  activityIds?: string[];
};

const InputsStatExport = ({ programme, activity, intervention, serviceProvider, step }: Props) => {
  const [portal] = useState(getPortal());
  const [state, setState] = useState<State>({});
  const [levelList, setLevelList] = useState<any>([]);
  const [activityList, setActivityList] = useState<any>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [headers, setHeaders] = useState<{ label: string; key: string }[] | undefined>(undefined);

  const { data: programmeData, error: programmeError, loading: programmeDataLoading } = useQuery(
    getAllProgrammesQuery(portal),
    {
      variables: {
        input: {
          isArchived: false,
          includeWhereReporter: true,
        },
      },
    }
  );
  const { data: spData, error: spError, loading: spDataLoading } = useQuery(ALL_SERVICE_PROVIDERS, {
    variables: {
      input: {
        ...(state.programmeIds && { programmeIds: state.programmeIds }),
      },
    },
  });
  const { data: interventionData, error: interventionError, loading: interventionDataLoading } = useQuery(
    ALL_INTERVENTIONS,
    {
      variables: {
        input: {
          ...(state.programmeIds && { programmeIds: state.programmeIds }),
          ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
        },
      },
    }
  );
  const { data: spActivitiesData, error: spActivitiesError, loading: spActivitiesLoading } = useQuery(
    portal === 'funder'
      ? ALL_FUNDER_ACTIVITIES
      : portal === 'service-provider'
      ? ALL_SERVICE_PROVIDER_ACTIVITIES
      : portal === 'institution'
      ? ALL_INSTITUTION_ACTIVITIES
      : ALL_SERVICE_PROVIDER_ACTIVITIES,
    { variables: { input: { interventionIds: state.interventionIds } } }
  );

  useEffect(() => {
    const { granularity, level } = state;

    if (granularity && level) {
      setLevelList(inputStatsVisibleOptions[granularity.value][level.value]);
    }

    if (granularity && level) {
      setHeaders(inputStatsSelectOptions[granularity.value][level.value]);
    }
  }, [state]);

  useEffect(() => {
    if (spActivitiesData && spActivitiesData.activities) {
      setActivityList(spActivitiesData.activities);
    }
  }, [spActivitiesData]);

  return (
    <Flex size="1" direction="column">
      <Fragment>
        <Flex size="1" direction="row" marginRight="10px">
          <StyledFormSelect
            isClearable={true}
            name="granularity"
            label="Granularity"
            onChange={(option: any) => {
              const newState = Object.assign({}, state, {
                granularity: option,
                level: null,
              });
              setState(newState);
            }}
            options={GRANULARITY}
          />
        </Flex>
        <Flex size="1" direction="row" marginRight="10px">
          <StyledFormSelect
            isClearable={true}
            name="level"
            label="Level"
            onChange={(option: any) => {
              const newState = Object.assign({}, state, {
                level: option,
              });
              setState(newState);
            }}
            value={state.level}
            options={INPUT_STATS_LEVELS[(state.granularity && state.granularity.value) || 'overall']}
          />
        </Flex>
      </Fragment>

      <Fragment>
        {levelList && levelList.includes('programme') && (
          <Flex size="1" direction="row" marginRight="10px">
            <StyledFormSelect
              isMulti={true}
              isClearable={true}
              name="programmeIds"
              label="Programme"
              placeholder={programmeDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
              isDisabled={programmeDataLoading}
              onChange={(option: any[]) => {
                const newState = Object.assign({}, state, {
                  [programme]: option ? option.map(opt => opt.value) : undefined,
                });
                setState(newState);
              }}
              options={
                programmeDataLoading || programmeError || !(programmeData && programmeData.programmes)
                  ? []
                  : programmeData.programmes.map((item: any) =>
                      formatSelectValue({
                        label: item.name,
                        value: item.id,
                      })
                    )
              }
            />
          </Flex>
        )}

        {levelList && levelList.includes('serviceProvider') && (
          <Flex size="1" direction="row" marginRight="10px">
            <StyledFormSelect
              isMulti={true}
              isClearable={true}
              name="serviceProviderIds"
              label="Service Provider"
              placeholder={spDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
              isDisabled={spDataLoading}
              onChange={(option: any[]) => {
                const newState = Object.assign({}, state, {
                  [serviceProvider]: option ? option.map(opt => opt.value) : undefined,
                });
                setState(newState);
              }}
              options={
                spDataLoading || spError || !(spData && spData.serviceProviders)
                  ? []
                  : spData.serviceProviders.map((item: any) =>
                      formatSelectValue({
                        label: item.name,
                        value: item.id,
                      })
                    )
              }
            />
          </Flex>
        )}

        {levelList && levelList.includes('intervention') && (
          <Flex size="1" direction="row" marginRight="10px">
            <StyledFormSelect
              isMulti={true}
              isClearable={true}
              name="interventionIds"
              label="Intervention"
              placeholder={interventionDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
              isDisabled={interventionDataLoading}
              onChange={(option: any[]) => {
                const newState = Object.assign({}, state, {
                  [intervention]: option ? option.map(opt => opt.value) : undefined,
                });
                setState(newState);
              }}
              options={
                interventionDataLoading || interventionError || !(interventionData && interventionData.allInterventions)
                  ? []
                  : interventionData.allInterventions.map((item: any) =>
                      formatSelectValue({
                        label: item.name,
                        value: item.id,
                      })
                    )
              }
            />
          </Flex>
        )}

        {levelList && levelList.includes('activity') && (
          <Flex size="1" direction="row" marginRight="10px">
            <StyledFormSelect
              isMulti={true}
              isClearable={true}
              name="activityIds"
              label="Activity"
              placeholder={spActivitiesLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
              isDisabled={spActivitiesLoading}
              onChange={(option: any[]) => {
                const newState = Object.assign({}, state, {
                  [activity]: option ? option.map(opt => opt.value) : undefined,
                });
                setState(newState);
              }}
              options={
                spActivitiesLoading || spActivitiesError || !(spActivitiesData && spActivitiesData.activities)
                  ? []
                  : spActivitiesData.activities.map((item: any) =>
                      formatSelectValue({ label: item.name, value: item.id })
                    )
              }
            />
          </Flex>
        )}

        <Flex size="1" direction="row" marginRight="10px">
          <StyledDatePicker
            name="activityDates"
            label="Dates"
            startDate={startDate || undefined}
            endDate={endDate || undefined}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
          />
        </Flex>
      </Fragment>

      <WrappedQuery
        query={EXPORT_INPUTS}
        variables={{
          input: {
            first: 10,
            ...(state.granularity && { granularity: state.granularity.value }),
            ...(state.level && { level: state.level.value }),
            ...(startDate && { startDate: startOfDay(new Date(startDate)) }),
            ...(endDate && { endDate: endOfDay(new Date(endDate)) }),
            ...(state.interventionIds && { interventionIds: state.interventionIds }),
            ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
            ...(state.programmeIds && { programmeIds: state.programmeIds }),
            ...(state.activityIds && { activityIds: state.activityIds }),
          },
        }}
        overrideStates={true}
      >
        {({ loading, error, data, fetchMore }) => {
          if (loading) {
            return <div>Preparing data for export...</div>;
          }

          if (error) {
            return <div>Something went wrong</div>;
          }

          // call the next page of the exportInputs query
          data.inputs &&
            data.inputs.pageInfo &&
            data.inputs.pageInfo.hasNextPage &&
            fetchMore({
              variables: {
                input: {
                  first: 10,
                  ...(state.granularity && { granularity: state.granularity.value }),
                  ...(state.level && { level: state.level }),
                  ...(startDate && { startDate: startOfDay(new Date(startDate)) }),
                  ...(endDate && { endDate: endOfDay(new Date(endDate)) }),
                  ...(state.interventionIds && { interventionIds: state.interventionIds }),
                  ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
                  ...(state.programmeIds && { programmeIds: state.programmeIds }),
                  ...(state.activityIds && { activityIds: state.activityIds }),
                  after: data.inputs && data.inputs.pageInfo && data.inputs.pageInfo.endCursor,
                },
              },
              updateQuery: (previousResult, { fetchMoreResult }) => {
                const newInputs = fetchMoreResult.inputs.edges;
                return {
                  inputs: {
                    aggregate: fetchMoreResult.inputs.aggregate,
                    pageInfo: fetchMoreResult.inputs.pageInfo,
                    edges: [...newInputs, ...previousResult.inputs.edges],
                    __typename: fetchMoreResult.inputs.__typename,
                  },
                };
              },
            });

          if (data.inputs && data.inputs.pageInfo && data.inputs.pageInfo.hasNextPage) {
            return <div>Fetching more pages...</div>;
          }

          const { granularity, level } = state;
          const formatted: any =
            data && data.inputs && granularity && level
              ? formatInputStatsData({
                  granularity: granularity.value,
                  level: level.value,
                  activityList,
                  data: data.inputs.edges,
                  startDate,
                  endDate,
                })
              : [];

          const sanitized = formatted.length ? sanitizeData(formatted) : [];
          return (
            <Flex size="1" direction="row" marginRight="10px">
              {sanitized && sanitized.length > 0 && (
                <CSVLink filename={'InputStatsExport.csv'} headers={headers} data={sanitized}>
                  Export CSV
                </CSVLink>
              )}

              {sanitized && sanitized.length < 1 && <span>No inputs data for given parameters</span>}
            </Flex>
          );
        }}
      </WrappedQuery>
    </Flex>
  );
};

export default InputsStatExport;
